/**
 * WizardLayout.jsx
 *
 * Shell that hosts the current wizard step. Provides slots for
 * ProgressDots (top), NavigationButtons (above the footer), and
 * CountsFooter (bottom).
 *
 * ENTRY suppresses everything (entry has a bespoke layout).
 * RESULTS suppresses ProgressDots + NavigationButtons + CountsFooter
 *   — once we're on the results page the user has a full product grid,
 *   the running count is redundant. We DO keep a top strip with a
 *   "Start over" link so the user can restart the wizard without
 *   having to dig through DevTools / sessionStorage.
 *
 * @module components/wizard/WizardLayout
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D1 — D6 wires in real components)
 * @updated 2026-05-18 (Vandana feedback): Start over now also shows on RESULTS.
 */

import { useWizard } from '@contexts/WizardContext';
import { STEPS } from '@components/wizard/wizardConstants';
import ProgressDots from './ProgressDots';
import NavigationButtons from './NavigationButtons';
import CountsFooter from './CountsFooter';

export default function WizardLayout({ children }) {
  const { currentStep, resetWizard } = useWizard();
  const isEntry = currentStep === STEPS.ENTRY;
  const isResults = currentStep === STEPS.RESULTS;

  return (
    <div className="wizard-shell min-h-[60vh] flex flex-col bg-white">
      {/* Top strip: progress dots (non-RESULTS) + Start over link. Hidden on ENTRY. */}
      {!isEntry && (
        <div className="py-4 border-b border-n2 relative min-h-[3rem]">
          {!isResults && <ProgressDots />}
          <button
            type="button"
            onClick={resetWizard}
            className="absolute top-3 right-4 text-xs text-n6 hover:text-electric-blue underline focus:outline-none focus:ring-2 focus:ring-electric-blue px-2 py-1"
          >
            Start over
          </button>
        </div>
      )}

      <main className="flex-1 py-8 px-4">{children}</main>

      {!isEntry && !isResults && <NavigationButtons />}
      {!isEntry && !isResults && <CountsFooter />}
    </div>
  );
}
